import { Injectable } from "@angular/core";
import { AgencyService } from "./agency/services/agency.service";
import { Helpers } from "../helpers/helpers";
import { NewCustomer } from "src/models/newcustomer";
import { Batch } from "src/models/agency";
import { AccountService } from "src/services/account.service";
import { firstValueFrom } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ComprehensiveBatchProcessService {
  private cloudEnabled: boolean | null = null;
  private products: string[] = [];
  private intervalId: any;
  private isChecking = false;

  constructor(
    private agencyService: AgencyService,
    private helper: Helpers,
    private accountService: AccountService,
  ) {
    if (this.helper.isAuthenticated()) {
      this.init();
    }
  }

  public init(): void {
    this.stopBackgroundTask();

    this.accountService.getAccount().subscribe({
      next: (account) => {
        this.cloudEnabled = account.agency.cloudEnabled;
        this.products = Array.isArray(account.agencyProducts)
          ? account.agencyProducts
          : [];

        if (this.shouldStartBackgroundTask()) {
          setTimeout(() => this.startBackgroundTask(), 5000);
        }
      },
      error: (err) => console.error(err),
    });
  }

  public stopBackgroundTask() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private startBackgroundTask() {
    this.stopBackgroundTask();
    const timeInterval = Math.floor(Math.random() * 5000) + 10000;
    this.intervalId = setInterval(() => {
      const userType = this.helper.getDecodedAccessToken(
        window.localStorage.token,
      )?.type;
      if (
        userType &&
        (userType.indexOf("LEVEL 0") === 0 ||
          userType.indexOf("LEVEL 1") === 0 ||
          userType.indexOf("LEVEL 2") === 0)
      ) {
        // To avoid spamming the server, we will check if the last processed time is more than 10 seconds ago
        const lastProcessed = window.localStorage.getItem("lastProcessed");
        if (
          !lastProcessed ||
          new Date(lastProcessed) < new Date(Date.now() - 10000)
        ) {
          window.localStorage.setItem(
            "lastProcessed",
            new Date().toISOString(),
          );
          this.checkComprehensiveBatchProcess();
        }
      }
    }, timeInterval); // 60,000 ms = 1 minute
  }

  private checkComprehensiveBatchProcess() {
    if (this.isChecking) {
      return;
    }

    this.isChecking = true;

    if (this.cloudEnabled) {
      // Fetch fresh agency data on every poll tick so we always see
      // the latest batch statuses set by the admin (e.g. PROCESSING).
      this.agencyService.getAgency().subscribe({
        next: (agency) => {
          const batches: Batch[] = agency.batches ?? [];

          const whatsappBatches = batches.filter(
            (batch) => batch.whatsAppStatus === "PROCESSING",
          );
          const emailBatches = batches.filter(
            (batch) => batch.emailStatus === "PROCESSING",
          );

          this.triggerCommunication("whatsapp", whatsappBatches);
          this.triggerCommunication("email", emailBatches);
          this.isChecking = false;
        },
        error: (err) => {
          console.error("Failed to fetch agency batches", err);
          this.isChecking = false;
        },
      });
    } else {
      this.agencyService.getBatches("PROCESSING", "whatsapp").subscribe({
        next: (res) => this.triggerCommunication("whatsapp", res),
        error: (err) =>
          console.error("Failed to fetch WhatsApp batches", err),
      });

      this.agencyService.getBatches("PROCESSING", "email").subscribe({
        next: (res) => this.triggerCommunication("email", res),
        error: (err) => console.error("Failed to fetch email batches", err),
      });

      this.checkRenewalProcess();
      this.isChecking = false;
    }
  }

  private checkRenewalProcess() {
    const today = new Date();

    if (today.getHours() < 8) {
      return;
    }

    if (this.cloudEnabled === true) {
      return;
    }

    const todayStr = today.toISOString().split("T")[0];
    const lastProcessed = window.localStorage.getItem(
      "lastRenewalProcessedDate",
    );

    if (lastProcessed === todayStr) {
      return;
    }

    this.agencyService.getBatches("PENDING", "renew").subscribe({
      next: async (batches) => {
        let processedAny = false;

        for (const batch of batches) {
          const success = await this.processRenewalForBatch(batch);
          if (success) {
            processedAny = true;
          }
        }

        if (processedAny) {
          localStorage.setItem("lastRenewalProcessedDate", todayStr);
        }
      },
    });
  }

  private async processRenewalForBatch(batch: Batch): Promise<boolean> {
    const importedData = this.helper.getMyImportedData();
    if (!importedData) {
      return false;
    }

    const batchData = importedData.filter((d) => d.batchId === batch.id);
    if (!batchData.length) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const group1d: NewCustomer[] = [];
    const group2df: NewCustomer[] = [];
    const group5d: NewCustomer[] = [];
    const group7d: NewCustomer[] = [];
    const group10d: NewCustomer[] = [];
    const group15d: NewCustomer[] = [];
    const group20d: NewCustomer[] = [];
    const group30d: NewCustomer[] = [];

    batchData.forEach((customer) => {
      if (!customer.expiryDate) return;

      const expiry = new Date(customer.expiryDate);
      expiry.setHours(0, 0, 0, 0);

      const diffDays = Math.ceil(
        (expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
      );

      if (diffDays === 1) {
        group1d.push(customer);
      } else if (diffDays === 5) {
        group5d.push(customer);
      } else if (diffDays === 7) {
        group7d.push(customer);
      } else if (diffDays === 10) {
        group10d.push(customer);
      } else if (diffDays === 15) {
        group15d.push(customer);
      } else if (diffDays === 20) {
        group20d.push(customer);
      } else if (diffDays === 30) {
        group30d.push(customer);
      } else if (diffDays === -2) {
        group2df.push(customer);
      }
    });

    if (group1d.length) {
      await firstValueFrom(
        this.agencyService.triggerCommunication(
          batch.id,
          "renew",
          undefined,
          undefined,
          group1d.map((c) => this.normalizeToISO(c)),
          "1d",
        ),
      );
    }

    if (group2df.length) {
      await firstValueFrom(
        this.agencyService.triggerCommunication(
          batch.id,
          "renew",
          undefined,
          undefined,
          group2df.map((c) => this.normalizeToISO(c)),
          "2df",
        ),
      );
    }

    if (group5d.length) {
      await firstValueFrom(
        this.agencyService.triggerCommunication(
          batch.id,
          "renew",
          undefined,
          undefined,
          group5d.map((c) => this.normalizeToISO(c)),
          "5d",
        ),
      );
    }

    if (group7d.length) {
      await firstValueFrom(
        this.agencyService.triggerCommunication(
          batch.id,
          "renew",
          undefined,
          undefined,
          group7d.map((c) => this.normalizeToISO(c)),
          "7d",
        ),
      );
    }

    if (group10d.length) {
      await firstValueFrom(
        this.agencyService.triggerCommunication(
          batch.id,
          "renew",
          undefined,
          undefined,
          group10d.map((c) => this.normalizeToISO(c)),
          "10d",
        ),
      );
    }

    if (group15d.length) {
      await firstValueFrom(
        this.agencyService.triggerCommunication(
          batch.id,
          "renew",
          undefined,
          undefined,
          group15d.map((c) => this.normalizeToISO(c)),
          "15d",
        ),
      );
    }

    if (group20d.length) {
      await firstValueFrom(
        this.agencyService.triggerCommunication(
          batch.id,
          "renew",
          undefined,
          undefined,
          group20d.map((c) => this.normalizeToISO(c)),
          "20d",
        ),
      );
    }

    if (group30d.length) {
      await firstValueFrom(
        this.agencyService.triggerCommunication(
          batch.id,
          "renew",
          undefined,
          undefined,
          group30d.map((c) => this.normalizeToISO(c)),
          "30d",
        ),
      );
    }

    return (
      group1d.length > 0 ||
      group2df.length > 0 ||
      group5d.length > 0 ||
      group7d.length > 0 ||
      group10d.length > 0 ||
      group15d.length > 0 ||
      group20d.length > 0 ||
      group30d.length > 0
    );
  }

  private triggerCommunication(
    channel: "whatsapp" | "email",
    batches: Batch[],
  ) {
    for (const batch of batches) {
      if (this.cloudEnabled) {
        const dataEntries = ((batch.batchData ?? []) as unknown as NewCustomer[])
          .map((c) => this.normalizeEntry(c));
        if (dataEntries.length > 0) {
          this.agencyService
            .triggerCommunication(batch.id, channel, undefined, dataEntries)
            .subscribe({
              next: () => {
                console.log(`Triggered ${channel} for batch ${batch.id}`);
              },
              error: (err) =>
                console.error(
                  `Failed to trigger ${channel} for batch ${batch.id}`,
                  err,
                ),
            });
        }
      } else {
        const importedData = this.helper.getMyImportedData();

        if (importedData) {
          const dataEntries: NewCustomer[] = [];
          for (const data of importedData) {
            if (
              data.batchId === batch.id &&
              ((data.whatsAppStatus !== "PROCESSED" &&
                channel === "whatsapp") ||
                (data.emailStatus !== "PROCESSED" && channel === "email"))
            ) {
              dataEntries.push(data);
            }
          }
          if (dataEntries.length > 0) {
            this.agencyService
              .triggerCommunication(
                batch.id,
                channel,
                undefined,
                dataEntries.map((c) => this.normalizeEntry(c)),
              )
              .subscribe(() => {
                this.helper.markAsProcessed(batch.id, channel);
              });
          } else {
            this.helper.markAsProcessed(batch.id, channel);
          }
        }
      }
    }
  }

  private shouldStartBackgroundTask(): boolean {
    const hasCareplus = this.products.some(
      (product) => product.toLowerCase() === "careplus",
    );
    return !hasCareplus;
  }

  public getProducts(): string[] {
    return this.products;
  }

  private normalizeEntry(customer: NewCustomer): NewCustomer {
    return {
      ...customer,
      chassisNumber: customer.chassisNumber != null
        ? String(customer.chassisNumber)
        : customer.chassisNumber,
      dateOfBirth: customer.dateOfBirth
        ? new Date(customer.dateOfBirth).toISOString()
        : customer.dateOfBirth,
    };
  }

  private normalizeToISO(customer: NewCustomer): NewCustomer {
    return {
      ...customer,
      chassisNumber: customer.chassisNumber != null
        ? String(customer.chassisNumber)
        : customer.chassisNumber,
      dateOfBirth: customer.dateOfBirth
        ? new Date(customer.dateOfBirth).toISOString()
        : customer.dateOfBirth,
      expiryDate: customer.expiryDate
        ? new Date(customer.expiryDate).toISOString()
        : null,
    };
  }
}
